import { cn } from '@client/utils/styling/cn';
import { Spin, type SpinProps } from 'antd';
import type { ReactNode } from 'react';
import { forwardRef } from 'react';
import { Spinner } from './Spinner';

type LoadingSize = 'small' | 'medium' | 'large';

/** Standalone spinners wait this long before appearing, so a quick load shows nothing at all. */
const STANDALONE_DELAY_MS = 200;

export interface LoadingProps extends Omit<SpinProps, 'size' | 'children'> {
    /** Spinner size. small = 16px, medium = 24px, large = 32px */
    size?: LoadingSize;
    /** When provided, renders in overlay mode — spinner appears over children. */
    children?: ReactNode;
    /**
     * Overlay mode only: keep the `Spin` wrapper in the DOM while `spinning` is false, as a raw antd
     * `Spin` does. Without it an idle overlay renders its children bare, so toggling `spinning`
     * remounts them and drops their local state; set it when the children hold state (a form, a
     * table, a scroll position) or when the wrapper's layout must not change between the two states.
     */
    keepWrapper?: boolean;
    /**
     * Standalone mode only: this spinner stands in for a whole page (returned in place of the page,
     * or by a route guard). It grows to fill the page layout's content area, which becomes a flex
     * column while it is a direct child, so the spinner centres in the visible content area instead of
     * hugging the header. Ignored in overlay mode.
     */
    fillPage?: boolean;
}

/**
 * Unified loading spinner wrapper for Ant Design `Spin`.
 *
 * Two modes:
 * - **Standalone** (no children): renders a centred spinner filling the parent's height, after a
 *   short `delay` so a quick load shows nothing. As a page's own loading state (returned in place
 *   of the page) pass `fillPage`, so the spinner centres in the visible content area. In a section
 *   with no height of its own (a tab panel, an empty card) pass `className="min-h-60"` instead, so
 *   the spinner sits in the space rather than on its top edge.
 * - **Overlay** (with children): wraps content with a spinner overlay.
 *
 * When `spinning` is explicitly `false` in overlay mode, children are rendered
 * directly without any Spin wrapper DOM, unless `keepWrapper` is set.
 *
 * Extends `SpinProps` — all native props (`indicator`, `delay`, `fullscreen`, etc.)
 * pass through to Ant Design `Spin`.
 *
 * Where the layout that is loading is known (a dashboard, a table, a form), prefer a
 * `SkeletonBlock` shaped like it.
 */
export const Loading = forwardRef<HTMLDivElement, LoadingProps>(
    (
        {
            size = 'medium',
            children,
            className,
            classNames,
            description,
            keepWrapper = false,
            fillPage = false,
            ...spinProps
        },
        ref,
    ) => {
        // A description already says what is loading, so the spinner does not announce it twice. antd
        // shows `description ?? tip`, so the legacy `tip` alias counts as a description too.
        const indicator = <Spinner indicator size={size} label={(description ?? spinProps.tip) ? null : undefined} />;

        // Overlay mode: wrap children with Spin
        if (children !== undefined) {
            // When explicitly not spinning, render children directly (no wrapper DOM).
            // Idle renders no wrapper DOM; `keepWrapper` opts out.
            if (spinProps.spinning === false && !keepWrapper) {
                return <>{children}</>;
            }
            return (
                <Spin
                    spinning={spinProps.spinning ?? true}
                    size={size}
                    indicator={indicator}
                    className={className}
                    classNames={classNames}
                    description={description}
                    {...spinProps}
                >
                    {children}
                </Spin>
            );
        }

        // Standalone mode: centred spinner. Childless (non-nested) Spin so the
        // description keeps its natural width and stays inside Spin's aria-live
        // region — the old empty-child nesting constrained the description to
        // the empty content's width, wrapping it into an unreadable column.
        const standaloneClassNames: SpinProps['classNames'] =
            typeof classNames === 'function'
                ? (info) => {
                      const resolvedClassNames = classNames(info);
                      return {
                          ...resolvedClassNames,
                          description: cn('mt-3 text-center text-muted-foreground', resolvedClassNames?.description),
                      };
                  }
                : {
                      ...classNames,
                      description: cn('mt-3 text-center text-muted-foreground', classNames?.description),
                  };

        // `data-page-loading` lets the page layout's content container become a flex column while
        // this spinner stands in for a page, so the `flex-1` loader can fill it. Section spinners
        // leave it off, so they never change the page's layout.
        return (
            <div
                ref={ref}
                data-page-loading={fillPage ? '' : undefined}
                className={cn('flex h-full items-center justify-center', fillPage && 'flex-1', className)}
            >
                <Spin
                    spinning
                    size={size}
                    delay={STANDALONE_DELAY_MS}
                    indicator={indicator}
                    description={description}
                    classNames={standaloneClassNames}
                    {...spinProps}
                />
            </div>
        );
    },
);

Loading.displayName = 'Loading';

export type { LoadingSize };
